import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { organizationService } from '@services/organizationService';

/**
 * REV-897: the status report moved from /consultant/clients/:orgId/status to
 * /orgs/:slug/reports/status when the consultant pages were retired (REV-449).
 *
 * Old links and bookmarks carry the organization's id, but the new route is
 * keyed by slug, so this looks the organization up and replaces the history
 * entry with the new address. If it cannot be found (deleted, or no longer a
 * member) it falls back to the organizations list rather than a dead page.
 */
export default function LegacyStatusReportRedirect() {
  const { t } = useTranslation();
  const { orgId } = useParams<{ orgId: string }>();
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      try {
        const org = orgId ? await organizationService.getById(orgId) : null;
        if (!cancelled) {
          setTarget(org?.slug ? `/orgs/${org.slug}/reports/status` : '/orgs');
        }
      } catch {
        if (!cancelled) {
          setTarget('/orgs');
        }
      }
    };
    void resolve();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (target) {
    return <Navigate to={target} replace />;
  }

  return (
    <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
      <CircularProgress aria-label={t('common.loading', 'Loading...')} />
    </Box>
  );
}
